import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Email from '@mui/icons-material/Email';
import EmailModal from '../EmailModal';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { Photographer } from '../../BookingsCalendarTypes';
import PhotographerProductsIcons from './PhotographerProductsIcons';
import Sms from '@mui/icons-material/Sms';
import SmsModal from '../SmsModal';
import Typography from '@mui/material/Typography';
import RouteIcon from '@mui/icons-material/Route';

type PhotographerListItemLabels = {
    sms: string;
    email: string;
    route: string;
};

type PhotographerListItemProps = {
    labels: PhotographerListItemLabels;
    photographer: Photographer;
    hasSmallRowSize: boolean;
    hasLargeRowSize: boolean;
    isActiveRow: boolean;
};

enum BookedEventType {
    available = 0,
    se360Booking = 1,
    scandphotosBooking = 2,
    blocked = 4,
    travel = 8
}

const PhotographerListItem = ({
    photographer,
    labels,
    hasSmallRowSize,
    hasLargeRowSize,
    isActiveRow
}: PhotographerListItemProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [showSmsModal, setShowSmsModal] = React.useState<boolean>(false);
    const [showEmailModal, setShowEmailModal] = React.useState<boolean>(false);

    let travelDistance = (photographer.travelDistance / 1000).toFixed(2);

    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleOpenSmsModal = () => {
        handleClose();
        setShowSmsModal(true);
    };

    const handleOpenEmailModal = () => {
        handleClose();
        setShowEmailModal(true);
    };

    let workEvents = photographer.bookedEvents.length
        ? photographer.bookedEvents
              .filter((event) => {
                  return (
                      event.type == BookedEventType.se360Booking ||
                      event.type == BookedEventType.scandphotosBooking
                  );
              })
              .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
        : [];

    const handleShowDirections = () => {
        handleClose();
        if (!workEvents.length) {
            return null;
        }

        let eventAddresses = workEvents.map((a) => a.coordinates);

        const photographerAddress = photographer.workingHours[0].coordinates;

        const directionsLink = `https://www.google.com/maps/dir/?api=1&origin=${photographerAddress}&destination=${photographerAddress}&travelmode=driving`;

        eventAddresses.length &&
            window.open(directionsLink + '&waypoints=' + eventAddresses.join('|'), '_blank');
    };

    return (
        <Box
            sx={{
                display: 'flex',
                backgroundColor: '#F5F5F5',
                borderRadius: '3px',
                alignItems: 'center',
                width: '100%',
                padding: '8px',
                height: '100%'
            }}
        >
            {hasLargeRowSize || !hasSmallRowSize || isActiveRow ? (
                <Avatar
                    sx={{ width: '30px', height: '30px', margin: '2px' }}
                    src={photographer.profileImageReference}
                />
            ) : null}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    margin: '0 0 0 3px'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%'
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Gilroy"',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            width: '80%',
                            margin: '0 0 0 3px'
                        }}
                    >
                        {photographer.name}
                    </Typography>
                    <Box>
                        <IconButton
                            id="connect-button"
                            aria-controls={open ? 'connect-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            onClick={handleClick}
                            sx={{ height: '20px', width: '25px', borderRadius: '4px' }}
                        >
                            <MoreHoriz
                                sx={{
                                    color: 'rgba(0, 0, 0, 0.54)'
                                }}
                            />
                        </IconButton>
                        <Menu
                            id="connect-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            MenuListProps={{
                                'aria-labelledby': 'connect-button'
                            }}
                        >
                            <MenuItem onClick={handleOpenSmsModal}>
                                <ListItemIcon>
                                    <Sms fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{labels.sms}</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={handleOpenEmailModal}>
                                <ListItemIcon>
                                    <Email fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{labels.email}</ListItemText>
                            </MenuItem>
                            {workEvents.length ? (
                                <MenuItem onClick={handleShowDirections}>
                                    <ListItemIcon>
                                        <RouteIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>{labels.route}</ListItemText>
                                </MenuItem>
                            ) : null}
                        </Menu>
                    </Box>
                </Box>
                {hasLargeRowSize || !hasSmallRowSize || isActiveRow ? (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Typography
                            sx={{
                                display: 'flex',
                                fontFamily: '"Gilroy"',
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                marginLeft: '3px',
                                marginRight: '12px'
                            }}
                        >
                            {travelDistance}km
                        </Typography>
                        <PhotographerProductsIcons products={photographer.availableForProducts} />
                    </Box>
                ) : null}
            </Box>
            {photographer.phoneNumber ? (
                <SmsModal
                    showSmsModal={showSmsModal}
                    closeSmsModal={() => setShowSmsModal(false)}
                    photographer={photographer}
                />
            ) : null}
            {photographer.email ? (
                <EmailModal
                    showEmailModal={showEmailModal}
                    closeEmailModal={() => setShowEmailModal(false)}
                    photographer={photographer}
                />
            ) : null}
        </Box>
    );
};

export default PhotographerListItem;
